import { toSeconds } from '@art-suite/art-core-ts';
import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  getScanDataInMemoryDeltaFromScanRoots,
  ScanDataInMemory,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

import { ScanEditSyncGate } from '../../apiScanAndTemplateState/ScanEditSyncGate';
import { useScanDataSyncModel } from './Models/ScanDataSyncModel';

const SAVE_DELTA_DEBOUNCE_TIME = 1000;

export const useSaveScanData = (
  scanId: string,
  optimizedScanRoot: ScanRootOptimized | undefined,
  updateCachedScanData: (scanData: ScanDataInMemory[]) => void | Promise<void>,
  setOptimizedScanRoot: (scanRoot: ScanRootOptimized) => void,
  editSyncGate?: ScanEditSyncGate,
) => {
  const lastSavedOptimizedScanRootRef = useRef<ScanRootOptimized | undefined>(undefined);
  const latestOptimizedScanRootRef = useRef<ScanRootOptimized | undefined>(undefined);
  // Every scan root this hook itself produced via setOptimizedScanRoot (below).
  // Distinguishes our own optimistic updates from external ones (e.g. a change
  // order's cancel action reverting scan data server-side and refetching it) -
  // see the resync note in the effect below. A set rather than just the last
  // one: a render can lag behind rapid edits, and one that has only caught up
  // with an earlier edit is still ours, not external (KAIDEV-4328).
  const ownOptimizedScanRootsRef = useRef(new WeakSet<ScanRootOptimized>());
  // Edits are applied one at a time, each to the result of the one before -
  // see the note in the returned callback below (KAIDEV-4328).
  const pendingSaveRef = useRef<Promise<unknown>>(Promise.resolve());
  // Likewise, cache-and-sync writes run one at a time - see the note in
  // updateScanDataToSync below (KAIDEV-4328).
  const pendingSyncRef = useRef<Promise<unknown>>(Promise.resolve());
  const syncScanData = useScanDataSyncModel();

  useEffect(() => {
    // Our own edits already advanced latestOptimizedScanRootRef when they were
    // made, possibly past the root this render shows - don't roll it back.
    if (optimizedScanRoot && ownOptimizedScanRootsRef.current.has(optimizedScanRoot)) return;
    // An incoming scan root that isn't one we set ourselves came from
    // outside this hook's own save path - most notably, canceling a change
    // order reverts scan data server-side and refetches it, but never goes
    // through updateScanDataToSync below. Without this resync,
    // lastSavedOptimizedScanRootRef keeps pointing at the pre-cancel data, so a
    // later edit that happens to land back on that same value diffs to zero
    // changes and is silently dropped - see KAIDEV-4127.
    latestOptimizedScanRootRef.current = optimizedScanRoot;
    lastSavedOptimizedScanRootRef.current = optimizedScanRoot;
  }, [optimizedScanRoot]);

  const syncScanDataDelta = async (updatedOptimizedScanRoot: ScanRootOptimized) => {
    if (!lastSavedOptimizedScanRootRef.current) {
      throw new Error('No current scan data available (saveDelta)');
    }
    const startTime = toSeconds();

    // Captured before any awaiting below - if a newer edit lands while this
    // callback is suspended (e.g. on the cache write, which is real async I/O
    // in production), its recordEdit() call bumps this. That tells us below
    // not to clear the gate on that newer edit's behalf - its own debounced
    // callback will clear it once IT reaches the sync queue. See KAIDEV-4175
    // PR review (Shane).
    const editEpochAtStart = editSyncGate?.getEditEpoch();

    // Compute delta comparing previous and updated optimized scan data
    const delta = getScanDataInMemoryDeltaFromScanRoots(scanId, lastSavedOptimizedScanRootRef.current, updatedOptimizedScanRoot);
    await updateCachedScanData(delta);

    // this will save the delta to the server - awaited so the records are
    // visible to a concurrent hasUnsyncedLocalEdits() query before we
    // consider this edit "reached the queue" below.
    await syncScanData.addScanDataToSync(scanId, delta);

    lastSavedOptimizedScanRootRef.current = updatedOptimizedScanRoot;

    // The edit has now reached the sync queue - a deferred refetch (see
    // ScanEditSyncGate.ts / useScanData.ts) can retry once the queue itself
    // drains. Only clear the flag if no newer edit landed while we were
    // awaiting above - that edit's own callback will clear it once it reaches
    // the queue in turn. See KAIDEV-4175.
    if (editSyncGate && editSyncGate.getEditEpoch() === editEpochAtStart) {
      editSyncGate.setHasPendingLocalEdit(false);
    }

    const endTime = toSeconds();
    console.log(
      'Saved ScanData delta: ' + delta.length + (delta.length === 0 ? ' - NO CHANGES DETECTED' : '') + ' - time: ' + ((endTime - startTime) * 1000 | 0) + 'ms',
    );
  };

  // A slow write (the cache write is real async I/O in production) can still be
  // in progress when the next edit's debounce fires. Run each write only after
  // the one before it: otherwise the newer one diffs against a baseline the
  // older one hasn't advanced yet, the two can finish in either order, and the
  // older one's records - put by id, last write wins - can overwrite the newer
  // ones in the sync queue (KAIDEV-4328).
  const updateScanDataToSync = useDebouncedCallback((updatedOptimizedScanRoot: ScanRootOptimized) => {
    const sync = pendingSyncRef.current.then(() => syncScanDataDelta(updatedOptimizedScanRoot));
    pendingSyncRef.current = sync.catch(() => {});
    return sync;
  }, SAVE_DELTA_DEBOUNCE_TIME);

  return useCallback((scanRootUpdater: (scanRoot: ScanRootOptimized) => ScanRootOptimized | Promise<ScanRootOptimized>): Promise<ScanRootOptimized> => {
    // A background refetch that lands before this edit reaches the sync queue
    // must defer rather than rebuild the screen from data that doesn't
    // include it yet - see ScanEditSyncGate.ts. Mark that immediately, before
    // anything else about this edit is computed.
    editSyncGate?.recordEdit();
    editSyncGate?.setHasPendingLocalEdit(true);

    // Rapid edits (e.g. tapping a quantity's "+" several times) can arrive
    // before the previous one has finished applying or React has re-rendered
    // with its result. Each one waits its turn and starts from the latest root
    // we produced, so none of them is built on stale data and silently
    // overwritten - see KAIDEV-4328.
    const save = pendingSaveRef.current.then(async () => {
      const currentOptimizedScanRoot = latestOptimizedScanRootRef.current;
      if (!currentOptimizedScanRoot) {
        throw new Error('No current scan data available (useSaveScanData) !!currentOptimizedScanRoot ' + !!currentOptimizedScanRoot);
      }

      if (!lastSavedOptimizedScanRootRef.current) lastSavedOptimizedScanRootRef.current = currentOptimizedScanRoot;

      // Apply the update mutation function to get new optimized scan data
      const updatedOptimizedScanRoot = await scanRootUpdater(currentOptimizedScanRoot);

      console.log('Wanting to save delta - (will be debounced to only one save)');
      updateScanDataToSync(updatedOptimizedScanRoot);

      latestOptimizedScanRootRef.current = updatedOptimizedScanRoot;
      ownOptimizedScanRootsRef.current.add(updatedOptimizedScanRoot);
      setOptimizedScanRoot(updatedOptimizedScanRoot);

      return updatedOptimizedScanRoot;
    });
    // A failed edit rejects for its caller but must not block the ones after it.
    pendingSaveRef.current = save.catch(() => {});
    return save;
  }, [scanId, updateCachedScanData, editSyncGate]);
};
